import React, { useState, useEffect, useRef } from 'react';
import {
  Box,
  Container,
  Typography,
  Button,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  Chip,
} from '@mui/material';
import {
  School,
  TrendingUp,
  Assessment,
  Group,
  ArrowForward,
  AutoAwesome,
  CheckCircle,
  EmojiEvents,
  Speed,
  Psychology,
  StarOutlined,
} from '@mui/icons-material';
import { Link } from 'react-router-dom';

// ─── Scroll-reveal hook ───────────────────────────────────────────────────────
function useScrollReveal() {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) { setVisible(true); obs.disconnect(); } },
      { threshold: 0.12 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return { ref, visible };
}

// ─── Animated counter ─────────────────────────────────────────────────────────
function AnimatedNumber({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const { ref, visible } = useScrollReveal();
  useEffect(() => {
    if (!visible) return;
    let start = 0;
    const step = Math.ceil(target / 60);
    const timer = setInterval(() => {
      start = Math.min(start + step, target);
      setCount(start);
      if (start >= target) clearInterval(timer);
    }, 18);
    return () => clearInterval(timer);
  }, [visible, target]);
  return (
    <span ref={ref}>
      {count.toLocaleString()}{suffix}
    </span>
  );
}

// ─── Reveal wrapper ───────────────────────────────────────────────────────────
function Reveal({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const { ref, visible } = useScrollReveal();
  return (
    <div
      ref={ref}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(28px)',
        transition: `opacity 0.6s ease ${delay}ms, transform 0.6s ease ${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

const HomePage: React.FC = () => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.down('md'));

  const features = [
    {
      icon: <Psychology sx={{ color: '#1976d2' }} />,
      title: 'AI-Powered Recommendations',
      description: 'Adaptive learning paths that evolve with you. Our AI identifies gaps and serves exactly the right content at the right time.',
      color: '#e3f2fd',
    },
    {
      icon: <EmojiEvents sx={{ color: '#ed6c02' }} />,
      title: 'Gamified Learning',
      description: 'Earn XP, unlock badges, and climb leaderboards. Learning is more effective when it feels like a game you actually want to play.',
      color: '#fff3e0',
    },
    {
      icon: <School sx={{ color: '#2e7d32' }} />,
      title: 'Curriculum-Aligned',
      description: 'Perfectly matched to Scottish CfE, English, and Indian curricula. Every topic maps to official learning outcomes.',
      color: '#e8f5e9',
    },
    {
      icon: <TrendingUp sx={{ color: '#7b1fa2' }} />,
      title: 'Track Every Step',
      description: 'Visual progress dashboards for students, parents, and teachers. Know exactly where you stand — always.',
      color: '#f3e5f5',
    },
    {
      icon: <Assessment sx={{ color: '#c62828' }} />,
      title: 'Smart Assessments',
      description: 'Quizzes and tests with instant feedback and detailed analytics. Practice until you ace it.',
      color: '#ffebee',
    },
    {
      icon: <Group sx={{ color: '#00838f' }} />,
      title: 'Family & Teachers',
      description: 'Parents monitor progress in real time. Teachers manage classes and spot struggling students early.',
      color: '#e0f7fa',
    },
  ];

  const steps = [
    {
      number: '01',
      title: 'Enrol in Your Course',
      description: 'Search our curriculum-aligned library and join a course with one click. Your teacher can also add you directly.',
    },
    {
      number: '02',
      title: 'Learn at Your Pace',
      description: 'Work through topics with AI-guided content, videos, and practice questions — on any device, any time.',
    },
    {
      number: '03',
      title: 'Track & Celebrate',
      description: 'See your progress grow, earn achievements, and share milestones with your family and teachers.',
    },
  ];

  const testimonials = [
    { quote: 'StudyStride helped my daughter go from failing Maths to a B in one term. The progress tracking kept us both motivated.', author: 'Sarah M.', role: 'Parent, Edinburgh' },
    { quote: "The gamification is genius — my students actually want to do homework now. I can see exactly who needs extra help.", author: 'Mr. Thomson', role: 'Maths Teacher, Glasgow' },
    { quote: "I can study whenever I want and the AI always finds what I need to improve. My confidence has shot up.", author: 'Aiden K.', role: 'S4 Student' },
  ];

  const stats = [
    { target: 50000, suffix: '+', label: 'Students Learning' },
    { target: 200, suffix: '+', label: 'Schools Enrolled' },
    { target: 98, suffix: '%', label: 'Satisfaction Rate' },
    { target: 3, suffix: ' Curricula', label: 'Supported' },
  ];

  // ─── Gradient text style ──────────────────────────────────────────────────
  const gradientText = {
    background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
    WebkitBackgroundClip: 'text',
    WebkitTextFillColor: 'transparent',
    backgroundClip: 'text',
    display: 'inline-block',
  };

  return (
    <Box sx={{ overflow: 'hidden', backgroundColor: '#ffffff' }}>

      {/* ═══════════════════════════════════════════════════════════
          HERO SECTION
      ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: 'linear-gradient(160deg, #f0f4ff 0%, #faf5ff 50%, #fff 100%)',
          pt: { xs: 6, md: 10 },
          pb: { xs: 6, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -120,
            right: -120,
            width: 480,
            height: 480,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(123,31,162,0.08) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -80,
            left: -80,
            width: 320,
            height: 320,
            borderRadius: '50%',
            background: 'radial-gradient(circle, rgba(25,118,210,0.07) 0%, transparent 70%)',
            pointerEvents: 'none',
          },
        }}
      >
        <Container maxWidth="xl">
          <Box
            sx={{
              display: 'flex',
              flexDirection: { xs: 'column', md: 'row' },
              alignItems: 'center',
              gap: { xs: 4, md: 8 },
            }}
          >
            {/* Left: Text */}
            <Box sx={{ flex: 1, minWidth: 0 }}>
              <Chip
                icon={<AutoAwesome sx={{ fontSize: '14px !important', color: '#7b1fa2 !important' }} />}
                label="AI-Powered EduTech Platform"
                size="small"
                sx={{
                  mb: 2,
                  backgroundColor: '#f3e5f5',
                  color: '#7b1fa2',
                  fontWeight: 600,
                  fontSize: '0.75rem',
                  border: '1px solid rgba(123,31,162,0.2)',
                }}
              />
              <Typography
                component="h1"
                sx={{
                  fontWeight: 800,
                  lineHeight: 1.1,
                  mb: 2,
                  fontSize: { xs: '2.2rem', sm: '2.8rem', md: '3.4rem', lg: '4rem' },
                  letterSpacing: '-1px',
                }}
              >
                Learn Smarter.{' '}
                <Box component="span" sx={gradientText}>
                  Stride Further.
                </Box>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{
                  mb: 4,
                  lineHeight: 1.6,
                  fontWeight: 400,
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  maxWidth: 520,
                }}
              >
                The AI-powered learning platform built for Scottish, English, and Indian curricula. 
                Personalised paths, gamified progress, and real-time insights — for students, parents, and teachers.
              </Typography>

              {/* CTA buttons */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mb: 4 }}>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                    boxShadow: '0 4px 20px rgba(25,118,210,0.35)',
                    borderRadius: 2,
                    '&:hover': {
                      background: 'linear-gradient(135deg, #1565c0 0%, #6a1b9a 100%)',
                      boxShadow: '0 6px 24px rgba(25,118,210,0.45)',
                      transform: 'translateY(-1px)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  Get Started Free
                </Button>
                <Button
                  component={Link}
                  to="/guest"
                  variant="outlined"
                  size="large"
                  sx={{
                    px: { xs: 3, md: 4 },
                    py: 1.5,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderColor: '#1976d2',
                    color: '#1976d2',
                    borderRadius: 2,
                    '&:hover': {
                      backgroundColor: 'rgba(25,118,210,0.06)',
                      borderColor: '#1565c0',
                    },
                  }}
                >
                  Try as Guest
                </Button>
              </Box>

              {/* Trust badges */}
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', alignItems: 'center' }}>
                {['Free to start', 'No credit card', 'Cancel anytime'].map((label) => (
                  <Box key={label} sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <CheckCircle sx={{ fontSize: 16, color: '#2e7d32' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500 }}>
                      {label}
                    </Typography>
                  </Box>
                ))}
              </Box>
            </Box>

            {/* Right: Dashboard preview card */}
            <Box
              sx={{
                flex: { xs: '0 0 auto', md: 1 },
                width: { xs: '100%', md: 'auto' },
                maxWidth: { xs: 440, md: 500 },
                mx: { xs: 'auto', md: 0 },
              }}
            >
              <Box
                sx={{
                  background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                  borderRadius: 4,
                  p: { xs: 2.5, md: 3 },
                  boxShadow: '0 24px 64px rgba(25,118,210,0.28)',
                  position: 'relative',
                  overflow: 'hidden',
                  '&::before': {
                    content: '""',
                    position: 'absolute',
                    top: -30,
                    right: -30,
                    width: 180,
                    height: 180,
                    borderRadius: '50%',
                    background: 'rgba(255,255,255,0.07)',
                  },
                }}
              >
                {/* Mock dashboard header */}
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2.5, gap: 1.5 }}>
                  <School sx={{ color: 'rgba(255,255,255,0.9)', fontSize: 22 }} />
                  <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.95rem' }}>
                    My Learning Dashboard
                  </Typography>
                  <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
                    {['#ff5f57', '#febc2e', '#28c840'].map((c, i) => (
                      <Box key={i} sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: c }} />
                    ))}
                  </Box>
                </Box>

                {/* XP Progress bar */}
                <Box sx={{ mb: 2 }}>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                    <Typography sx={{ color: 'rgba(255,255,255,0.8)', fontSize: '0.78rem' }}>
                      Weekly XP
                    </Typography>
                    <Typography sx={{ color: 'white', fontSize: '0.78rem', fontWeight: 700 }}>
                      740 / 1000
                    </Typography>
                  </Box>
                  <Box sx={{ height: 8, bgcolor: 'rgba(255,255,255,0.2)', borderRadius: 4, overflow: 'hidden' }}>
                    <Box
                      sx={{
                        width: '74%',
                        height: '100%',
                        bgcolor: '#4ade80',
                        borderRadius: 4,
                        boxShadow: '0 0 8px rgba(74,222,128,0.6)',
                      }}
                    />
                  </Box>
                </Box>

                {/* Stats grid */}
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 1.5, mb: 2 }}>
                  {[
                    { label: 'Topics Done', value: '23', icon: '✅' },
                    { label: 'Current Streak', value: '14 days', icon: '🔥' },
                    { label: 'Quiz Score', value: '91%', icon: '⚡' },
                    { label: 'Badges Earned', value: '8', icon: '🏅' },
                  ].map((s) => (
                    <Box
                      key={s.label}
                      sx={{
                        bgcolor: 'rgba(255,255,255,0.12)',
                        borderRadius: 2,
                        p: 1.5,
                        backdropFilter: 'blur(8px)',
                      }}
                    >
                      <Typography sx={{ fontSize: '1.1rem', mb: 0.2 }}>{s.icon}</Typography>
                      <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '1.05rem', lineHeight: 1 }}>
                        {s.value}
                      </Typography>
                      <Typography sx={{ color: 'rgba(255,255,255,0.65)', fontSize: '0.7rem' }}>
                        {s.label}
                      </Typography>
                    </Box>
                  ))}
                </Box>

                {/* Recent activity */}
                <Box sx={{ bgcolor: 'rgba(255,255,255,0.1)', borderRadius: 2, p: 1.5 }}>
                  <Typography sx={{ color: 'rgba(255,255,255,0.7)', fontSize: '0.72rem', mb: 1, fontWeight: 600, letterSpacing: '0.05em', textTransform: 'uppercase' }}>
                    Recent Activity
                  </Typography>
                  {[
                    { topic: 'Quadratic Equations', score: '95%', color: '#4ade80' },
                    { topic: 'Forces & Motion', score: '82%', color: '#60a5fa' },
                  ].map((a) => (
                    <Box key={a.topic} sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography sx={{ color: 'rgba(255,255,255,0.85)', fontSize: '0.78rem' }}>{a.topic}</Typography>
                      <Typography sx={{ color: a.color, fontSize: '0.78rem', fontWeight: 700 }}>{a.score}</Typography>
                    </Box>
                  ))}
                </Box>

                {/* Floating badge */}
                <Box
                  sx={{
                    position: 'absolute',
                    bottom: -12,
                    right: 24,
                    bgcolor: 'white',
                    borderRadius: 3,
                    px: 1.5,
                    py: 0.8,
                    boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 0.8,
                  }}
                >
                  <StarOutlined sx={{ color: '#ed6c02', fontSize: 16 }} />
                  <Typography sx={{ fontSize: '0.75rem', fontWeight: 700, color: '#1a1a1a' }}>
                    Top 10% this week!
                  </Typography>
                </Box>
              </Box>
            </Box>
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          SOCIAL PROOF BAR
      ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          borderTop: '1px solid',
          borderBottom: '1px solid',
          borderColor: 'grey.100',
          py: { xs: 4, md: 5 },
          backgroundColor: '#fafafa',
        }}
      >
        <Container maxWidth="lg">
          <Reveal>
            <Typography
              variant="overline"
              sx={{ display: 'block', textAlign: 'center', color: 'text.disabled', mb: 3, letterSpacing: '0.12em' }}
            >
              Trusted by students & schools across 3 countries
            </Typography>
          </Reveal>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
              gap: { xs: 3, md: 2 },
              textAlign: 'center',
            }}
          >
            {stats.map((stat, i) => (
              <Reveal key={stat.label} delay={i * 100}>
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: { xs: '1.8rem', md: '2.2rem' },
                    ...gradientText,
                    mb: 0.3,
                  }}
                >
                  <AnimatedNumber target={stat.target} suffix={stat.suffix} />
                </Typography>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 500 }}>
                  {stat.label}
                </Typography>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          FEATURES SECTION
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  mb: 1.5,
                  letterSpacing: '-0.5px',
                }}
              >
                Everything you need to{' '}
                <Box component="span" sx={gradientText}>
                  excel
                </Box>
              </Typography>
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ maxWidth: 560, mx: 'auto', fontWeight: 400, fontSize: { xs: '1rem', md: '1.1rem' } }}
              >
                Built for students who want to progress, parents who want visibility, and teachers who need tools that work.
              </Typography>
            </Box>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {features.map((feature, i) => (
              <Reveal key={feature.title} delay={i * 80}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    borderRadius: 3,
                    p: 0.5,
                    transition: 'all 0.25s ease',
                    '&:hover': {
                      transform: 'translateY(-4px)',
                      boxShadow: '0 12px 40px rgba(0,0,0,0.08)',
                      borderColor: 'grey.200',
                    },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        mb: 1.5,
                      }}
                    >
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: 2,
                          backgroundColor: feature.color,
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                          '& .MuiSvgIcon-root': { fontSize: 24 },
                        }}
                      >
                        {feature.icon}
                      </Box>
                      <Typography variant="h6" sx={{ fontWeight: 700, fontSize: '1rem', lineHeight: 1.3 }}>
                        {feature.title}
                      </Typography>
                    </Box>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7 }}>
                      {feature.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          HOW IT WORKS
      ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          py: { xs: 7, md: 10 },
          background: 'linear-gradient(160deg, #f0f4ff 0%, #faf5ff 100%)',
        }}
      >
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  mb: 1.5,
                  letterSpacing: '-0.5px',
                }}
              >
                Up and running in{' '}
                <Box component="span" sx={gradientText}>
                  3 steps
                </Box>
              </Typography>
              <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 400, fontSize: { xs: '1rem', md: '1.1rem' } }}>
                No lengthy setup. Just sign up and start learning today.
              </Typography>
            </Box>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: { xs: 4, md: 5 },
              position: 'relative',
            }}
          >
            {/* Connector line — desktop only */}
            {!isTablet && (
              <Box
                sx={{
                  position: 'absolute',
                  top: 36,
                  left: 'calc(33.33% + 36px)',
                  right: 'calc(33.33% + 36px)',
                  height: 2,
                  background: 'linear-gradient(90deg, #1976d2, #7b1fa2)',
                  opacity: 0.25,
                  zIndex: 0,
                }}
              />
            )}

            {steps.map((step, i) => (
              <Reveal key={step.number} delay={i * 120}>
                <Box sx={{ position: 'relative', zIndex: 1, textAlign: { xs: 'left', md: 'center' }, display: 'flex', gap: { xs: 2, md: 0 }, flexDirection: { xs: 'row', md: 'column' }, alignItems: { xs: 'flex-start', md: 'center' } }}>
                  <Box
                    sx={{
                      width: 72,
                      height: 72,
                      borderRadius: '50%',
                      background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      mb: { xs: 0, md: 2.5 },
                      flexShrink: 0,
                      boxShadow: '0 4px 20px rgba(25,118,210,0.3)',
                    }}
                  >
                    <Typography sx={{ color: 'white', fontWeight: 800, fontSize: '1.1rem' }}>
                      {step.number}
                    </Typography>
                  </Box>
                  <Box>
                    <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.8, textAlign: { xs: 'left', md: 'center' } }}>
                      {step.title}
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ lineHeight: 1.7, textAlign: { xs: 'left', md: 'center' } }}>
                      {step.description}
                    </Typography>
                  </Box>
                </Box>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          TESTIMONIALS
      ═══════════════════════════════════════════════════════════ */}
      <Box sx={{ py: { xs: 7, md: 10 }, backgroundColor: '#fff' }}>
        <Container maxWidth="lg">
          <Reveal>
            <Box sx={{ textAlign: 'center', mb: { xs: 5, md: 7 } }}>
              <Typography
                component="h2"
                sx={{
                  fontWeight: 800,
                  fontSize: { xs: '1.8rem', md: '2.5rem' },
                  mb: 1.5,
                  letterSpacing: '-0.5px',
                }}
              >
                Students, parents &{' '}
                <Box component="span" sx={gradientText}>
                  teachers love it
                </Box>
              </Typography>
            </Box>
          </Reveal>

          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
              gap: 3,
            }}
          >
            {testimonials.map((t, i) => (
              <Reveal key={t.author} delay={i * 100}>
                <Card
                  elevation={0}
                  sx={{
                    height: '100%',
                    border: '1px solid',
                    borderColor: 'grey.100',
                    borderRadius: 3,
                    p: 0.5,
                    transition: 'box-shadow 0.25s',
                    '&:hover': { boxShadow: '0 8px 32px rgba(0,0,0,0.07)' },
                  }}
                >
                  <CardContent sx={{ p: { xs: 2.5, md: 3 } }}>
                    <Box sx={{ display: 'flex', gap: 0.3, mb: 2 }}>
                      {Array.from({ length: 5 }).map((_, idx) => (
                        <StarOutlined key={idx} sx={{ color: '#ed6c02', fontSize: 18 }} />
                      ))}
                    </Box>
                    <Typography
                      variant="body2"
                      sx={{ lineHeight: 1.75, mb: 2.5, color: 'text.primary', fontStyle: 'italic' }}
                    >
                      "{t.quote}"
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                      <Box
                        sx={{
                          width: 40,
                          height: 40,
                          borderRadius: '50%',
                          background: 'linear-gradient(135deg, #1976d2, #7b1fa2)',
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          flexShrink: 0,
                        }}
                      >
                        <Typography sx={{ color: 'white', fontWeight: 700, fontSize: '0.85rem' }}>
                          {t.author.charAt(0)}
                        </Typography>
                      </Box>
                      <Box>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>{t.author}</Typography>
                        <Typography variant="caption" color="text.secondary">{t.role}</Typography>
                      </Box>
                    </Box>
                  </CardContent>
                </Card>
              </Reveal>
            ))}
          </Box>
        </Container>
      </Box>

      {/* ═══════════════════════════════════════════════════════════
          FINAL CTA
      ═══════════════════════════════════════════════════════════ */}
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1976d2 0%, #7b1fa2 100%)',
          py: { xs: 7, md: 10 },
          position: 'relative',
          overflow: 'hidden',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: -60,
            right: -60,
            width: 300,
            height: 300,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.06)',
          },
          '&::after': {
            content: '""',
            position: 'absolute',
            bottom: -80,
            left: -40,
            width: 240,
            height: 240,
            borderRadius: '50%',
            background: 'rgba(255,255,255,0.04)',
          },
        }}
      >
        <Container maxWidth="md">
          <Reveal>
            <Box sx={{ textAlign: 'center', position: 'relative', zIndex: 1 }}>
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 0.8,
                  bgcolor: 'rgba(255,255,255,0.15)',
                  borderRadius: 3,
                  px: 2,
                  py: 0.8,
                  mb: 3,
                  border: '1px solid rgba(255,255,255,0.25)',
                }}
              >
                <Speed sx={{ color: 'white', fontSize: 16 }} />
                <Typography sx={{ color: 'white', fontSize: '0.8rem', fontWeight: 600 }}>
                  Start in under 60 seconds
                </Typography>
              </Box>
              <Typography
                component="h2"
                sx={{
                  color: 'white',
                  fontWeight: 800,
                  fontSize: { xs: '1.9rem', sm: '2.4rem', md: '3rem' },
                  mb: 2,
                  lineHeight: 1.15,
                  letterSpacing: '-0.5px',
                }}
              >
                Ready to start your learning journey?
              </Typography>
              <Typography
                sx={{
                  color: 'rgba(255,255,255,0.8)',
                  fontSize: { xs: '1rem', md: '1.15rem' },
                  mb: 4,
                  maxWidth: 520,
                  mx: 'auto',
                  lineHeight: 1.7,
                }}
              >
                Join 50,000+ students already tracking their progress and reaching their goals with StudyStride.
              </Typography>
              <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', justifyContent: 'center' }}>
                <Button
                  component={Link}
                  to="/signup"
                  variant="contained"
                  size="large"
                  endIcon={<ArrowForward />}
                  sx={{
                    px: { xs: 3.5, md: 5 },
                    py: 1.75,
                    fontSize: '1rem',
                    fontWeight: 700,
                    backgroundColor: 'white',
                    color: '#1976d2',
                    borderRadius: 2,
                    boxShadow: '0 4px 20px rgba(0,0,0,0.2)',
                    '&:hover': {
                      backgroundColor: '#f5f5f5',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 6px 28px rgba(0,0,0,0.25)',
                    },
                    transition: 'all 0.2s',
                  }}
                >
                  Start Learning Free
                </Button>
                <Button
                  component={Link}
                  to="/guest"
                  variant="outlined"
                  size="large"
                  sx={{
                    px: { xs: 3.5, md: 5 },
                    py: 1.75,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderColor: 'rgba(255,255,255,0.6)',
                    color: 'white',
                    borderRadius: 2,
                    '&:hover': {
                      borderColor: 'white',
                      backgroundColor: 'rgba(255,255,255,0.1)',
                    },
                  }}
                >
                  Explore as Guest
                </Button>
              </Box>
            </Box>
          </Reveal>
        </Container>
      </Box>

    </Box>
  );
};

export default HomePage;
